import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Grip,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  QrCode,
  RotateCcw,
  Square,
  Trash2,
  Type,
} from "lucide-react";

const PDFJS_LIBRARY_URL =
  process.env.REACT_APP_PDFJS_LIBRARY_URL ||
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs";
const PDFJS_WORKER_URL =
  process.env.REACT_APP_PDFJS_WORKER_URL ||
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs";

let pdfJsPromise = null;
const loadPdfJs = async () => {
  if (!pdfJsPromise) {
    pdfJsPromise = import(/* webpackIgnore: true */ PDFJS_LIBRARY_URL).then(
      (pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        return pdfjs;
      }
    );
  }
  return pdfJsPromise;
};

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const textField = (overrides) => ({
  type: "text",
  enabled: false,
  page: 0,
  x: 0.2,
  y: 0.45,
  width: 0.6,
  height: 0.06,
  fontSize: 18,
  minFontSize: 7,
  fontFamily: "Helvetica",
  alignment: "center",
  verticalAlignment: "middle",
  color: "#172C4A",
  uppercase: false,
  maxLines: 1,
  lineHeight: 1.18,
  prefix: "",
  suffix: "",
  ...overrides,
});

export const createDefaultCertificateTemplateLayout = ({
  orientation = "landscape",
  pageCount = 1,
} = {}) => {
  const portrait = orientation === "portrait";
  return {
    version: 2,
    configured: false,
    coordinateSystem: "normalized-top-left",
    pageCount,
    fields: {
      participantName: textField({
        enabled: true,
        x: portrait ? 0.14 : 0.24,
        y: portrait ? 0.3 : 0.33,
        width: portrait ? 0.72 : 0.52,
        height: 0.09,
        fontSize: portrait ? 30 : 34,
        minFontSize: 14,
        fontFamily: "Helvetica-Bold",
      }),
      theme: textField({
        x: 0.2,
        y: portrait ? 0.45 : 0.47,
        width: 0.6,
        height: 0.05,
        fontSize: 17,
        minFontSize: 9,
        fontFamily: "Helvetica-Bold",
      }),
      subTheme: textField({
        enabled: true,
        x: portrait ? 0.12 : 0.2,
        y: portrait ? 0.5 : 0.51,
        width: portrait ? 0.76 : 0.6,
        height: portrait ? 0.09 : 0.07,
        fontSize: portrait ? 17 : 19,
        minFontSize: 8,
        maxLines: 2,
      }),
      training: textField({
        x: 0.18,
        y: 0.49,
        width: 0.64,
        height: 0.08,
        fontSize: 18,
        minFontSize: 8,
        maxLines: 2,
      }),
      dateRange: textField({
        enabled: true,
        x: portrait ? 0.2 : 0.34,
        y: portrait ? 0.61 : 0.59,
        width: portrait ? 0.6 : 0.32,
        height: 0.045,
        fontSize: 14,
        minFontSize: 8,
      }),
      startDate: textField({
        x: 0.3,
        y: 0.59,
        width: 0.18,
        height: 0.04,
        fontSize: 13,
      }),
      endDate: textField({
        x: 0.52,
        y: 0.59,
        width: 0.18,
        height: 0.04,
        fontSize: 13,
      }),
      duration: textField({
        x: 0.4,
        y: 0.65,
        width: 0.2,
        height: 0.04,
        fontSize: 12,
      }),
      certificateId: textField({
        enabled: true,
        x: portrait ? 0.24 : 0.18,
        y: 0.92,
        width: portrait ? 0.4 : 0.26,
        height: 0.035,
        fontSize: 9,
        minFontSize: 6,
        alignment: "left",
        color: "#5F6670",
      }),
      organizationName: textField({
        x: 0.3,
        y: 0.77,
        width: 0.4,
        height: 0.04,
        fontSize: 12,
        fontFamily: "Helvetica-Bold",
      }),
      issueDate: textField({
        x: 0.12,
        y: 0.74,
        width: 0.22,
        height: 0.04,
        fontSize: 11,
        alignment: "left",
      }),
      qrCode: {
        type: "qr",
        enabled: true,
        page: 0,
        x: portrait ? 0.07 : 0.065,
        y: portrait ? 0.78 : 0.79,
        width: portrait ? 0.16 : 0.085,
        height: portrait ? 0.11 : 0.13,
      },
    },
    maskZones: [],
  };
};

const FIELD_DEFINITIONS = [
  ["participantName", "participantName", "Yomna Ksontini"],
  ["theme", "theme", "Management de la qualité"],
  ["subTheme", "subTheme", "Auditeur interne ISO 9001:2015"],
  ["training", "training", "Auditeur interne ISO 9001:2015"],
  ["dateRange", "dateRange", "25/07/2026 - 26/07/2026"],
  ["startDate", "startDate", "25/07/2026"],
  ["endDate", "endDate", "26/07/2026"],
  ["duration", "duration", "2 jours"],
  ["certificateId", "certificateId", "CERT-1785160385672-3297"],
  ["organizationName", "organizationName", "Organisation Exemple"],
  ["issueDate", "issueDate", "27/07/2026"],
  ["qrCode", "qrCode", "QR"],
];

const COPY = {
  fr: {
    loading: "Chargement du PDF...",
    loadError: "Impossible d’afficher ce PDF dans l’éditeur.",
    fields: "Champs dynamiques",
    masks: "Zones de masquage",
    addMask: "Ajouter une zone blanche",
    noMask: "Aucune zone de masquage sur cette page.",
    settings: "Réglages du champ",
    page: "Page",
    fontSize: "Taille de police",
    font: "Police",
    alignment: "Alignement",
    left: "Gauche",
    center: "Centre",
    right: "Droite",
    color: "Couleur",
    maxLines: "Lignes maximum",
    uppercase: "Majuscules",
    validate: "Valider la mise en page",
    validated: "Mise en page validée",
    mustValidate: "Positionne les champs puis valide la mise en page.",
    validationError: "Le nom du participant doit obligatoirement rester coché.",
    reset: "Réinitialiser",
    previous: "Page précédente",
    next: "Page suivante",
    dragHint: "Glisse les blocs sur le PDF et redimensionne-les avec la poignée en bas à droite.",
    maskHint: "Les zones blanches couvrent les textes d’exemple déjà présents dans le PDF.",
    participantName: "Nom du participant",
    theme: "Thème",
    subTheme: "Sous-thème",
    training: "Formation",
    dateRange: "Période complète",
    startDate: "Date de début",
    endDate: "Date de fin",
    duration: "Durée",
    certificateId: "Identifiant",
    organizationName: "Nom de l’organisation",
    issueDate: "Date d’émission",
    qrCode: "QR code",
    deleteMask: "Supprimer la zone",
    zoom: "Zoom",
    normalSize: "100 %",
    enlarge: "Agrandir l’éditeur",
    closeFullscreen: "Quitter le plein écran",
    fullscreenHint: "Utilise le plein écran et le zoom pour positionner précisément chaque élément.",
    chooseFields: "Coche uniquement les éléments qui doivent apparaître sur les futurs certificats.",
    required: "Obligatoire",
    optional: "Optionnel",
    included: "Affiché",
    excluded: "Masqué",
    participantNameRequired: "Le nom du participant est obligatoire et ne peut pas être décoché.",

  },
  en: {
    loading: "Loading PDF...",
    loadError: "Unable to display this PDF in the editor.",
    fields: "Dynamic fields",
    masks: "Mask zones",
    addMask: "Add a white mask",
    noMask: "No mask zone on this page.",
    settings: "Field settings",
    page: "Page",
    fontSize: "Font size",
    font: "Font",
    alignment: "Alignment",
    left: "Left",
    center: "Center",
    right: "Right",
    color: "Color",
    maxLines: "Maximum lines",
    uppercase: "Uppercase",
    validate: "Validate layout",
    validated: "Layout validated",
    mustValidate: "Position the fields and then validate the layout.",
    validationError: "The participant name must remain selected.",
    reset: "Reset",
    previous: "Previous page",
    next: "Next page",
    dragHint: "Drag blocks over the PDF and resize them with the bottom-right handle.",
    maskHint: "White masks cover sample text already embedded in the PDF.",
    participantName: "Participant name",
    theme: "Theme",
    subTheme: "Sub-theme",
    training: "Training",
    dateRange: "Full period",
    startDate: "Start date",
    endDate: "End date",
    duration: "Duration",
    certificateId: "Identifier",
    organizationName: "Organization name",
    issueDate: "Issue date",
    qrCode: "QR code",
    deleteMask: "Delete mask",
    zoom: "Zoom",
    normalSize: "100%",
    enlarge: "Enlarge editor",
    closeFullscreen: "Exit fullscreen",
    fullscreenHint: "Use fullscreen and zoom to position every element precisely.",
    chooseFields: "Select only the elements that must appear on future certificates.",
    required: "Required",
    optional: "Optional",
    included: "Visible",
    excluded: "Hidden",
    participantNameRequired: "The participant name is required and cannot be unchecked.",

  },
};

const normalizeIncomingLayout = (layout, orientation, pageCount) => {
  const defaults = createDefaultCertificateTemplateLayout({
    orientation,
    pageCount,
  });

  if (!layout?.fields) {
    return {
      ...defaults,
      fields: {
        ...defaults.fields,
        participantName: {
          ...defaults.fields.participantName,
          enabled: true,
        },
      },
    };
  }

  const fields = {
    ...defaults.fields,
    ...layout.fields,
  };

  // Le nom du participant est le seul champ obligatoire.
  fields.participantName = {
    ...defaults.fields.participantName,
    ...fields.participantName,
    enabled: true,
  };

  return {
    ...defaults,
    ...layout,
    version: 2,
    pageCount,
    fields,
    maskZones: Array.isArray(layout.maskZones) ? layout.maskZones : [],
  };
};

const validateLayout = (layout) =>
  Boolean(layout?.fields?.participantName?.enabled);

const qrPattern = Array.from({ length: 8 }, (_, row) =>
  Array.from({ length: 8 }, (_, column) => (row * 7 + column * 11) % 3 !== 0)
);

const CertificateTemplateDesigner = ({
  file,
  initialLayout,
  onChange,
  language = "fr",
  organizationName = "Organisation Exemple",
  defaultFullscreen = false,
}) => {
  const copy = COPY[language === "en" ? "en" : "fr"];
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const pdfRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [orientation, setOrientation] = useState("landscape");
  const [layout, setLayout] = useState(() =>
    normalizeIncomingLayout(initialLayout, "landscape", 1)
  );
  const [selection, setSelection] = useState({ kind: "field", key: "participantName" });
  const [validationMessage, setValidationMessage] = useState("");
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(defaultFullscreen);

  const previewBaseWidth = orientation === "portrait" ? 820 : 1200;
  const previewWidth = Math.round(previewBaseWidth * zoom);

  const changeZoom = (nextZoom) => {
    setZoom(clamp(Number(nextZoom) || 1, 0.6, 2));
  };

  const emit = (next, markDirty = true) => {
    const emitted = {
      ...next,
      configured: markDirty ? false : next.configured,
    };
    setLayout(emitted);
    onChange?.(emitted);
  };

  useEffect(() => {
    if (!isFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const pdfjs = await loadPdfJs();
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }

        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageIndex(0);
        const firstPage = await pdf.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1 });
        const nextOrientation = firstViewport.width >= firstViewport.height
          ? "landscape"
          : "portrait";
        setOrientation(nextOrientation);

        const nextLayout = normalizeIncomingLayout(
          initialLayout,
          nextOrientation,
          pdf.numPages
        );
        setLayout(nextLayout);
        onChange?.(nextLayout);
      } catch (error) {
        console.error("CertificateTemplateDesigner PDF load error:", error);
        setLoadError(error?.message || copy.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
      pdfRef.current?.destroy?.();
      pdfRef.current = null;
    };
  }, [file]);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      if (!pdfRef.current || !canvasRef.current) return;
      try {
        renderTaskRef.current?.cancel?.();
        const page = await pdfRef.current.getPage(pageIndex + 1);
        const naturalViewport = page.getViewport({ scale: 1 });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const targetPixelWidth = Math.min(
          2800,
          Math.max(1400, previewWidth * pixelRatio)
        );
        const scale = targetPixelWidth / naturalViewport.width;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d", { alpha: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (error) {
        if (!cancelled && error?.name !== "RenderingCancelledException") {
          console.error("CertificateTemplateDesigner render error:", error);
          setLoadError(error?.message || copy.loadError);
        }
      }
    };
    render();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [pageIndex, pageCount, previewWidth]);

  const fieldDefinitions = useMemo(
    () =>
      FIELD_DEFINITIONS.map(([key, labelKey, sample]) => ({
        key,
        label: copy[labelKey],
        sample:
          key === "organizationName" ? organizationName || sample : sample,
      })),
    [copy, organizationName]
  );

  const updateField = (key, patch) => {
    const safePatch =
      key === "participantName" && patch?.enabled === false
        ? { ...patch, enabled: true }
        : patch;

    const next = {
      ...layout,
      fields: {
        ...layout.fields,
        [key]: {
          ...layout.fields[key],
          ...safePatch,
        },
      },
    };

    emit(next, true);
  };

  const toggleField = (key, checked) => {
    if (key === "participantName" && !checked) {
      setValidationMessage(copy.participantNameRequired);
      setSelection({ kind: "field", key: "participantName" });
      return;
    }

    setValidationMessage("");
    updateField(key, {
      enabled: checked,
      page: pageIndex,
    });

    if (checked) {
      setSelection({ kind: "field", key });
      return;
    }

    if (selection.kind === "field" && selection.key === key) {
      setSelection({ kind: "field", key: "participantName" });
    }
  };

  const updateMask = (id, patch) => {
    emit(
      {
        ...layout,
        maskZones: layout.maskZones.map((mask) =>
          mask.id === id ? { ...mask, ...patch } : mask
        ),
      },
      true
    );
  };

  const startInteraction = (event, target, mode) => {
    event.preventDefault();
    event.stopPropagation();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const source = target.kind === "field"
      ? layout.fields[target.key]
      : layout.maskZones.find((item) => item.id === target.id);
    if (!source) return;

    setSelection(target);
    const startX = event.clientX;
    const startY = event.clientY;
    const original = { ...source };

    const onMove = (moveEvent) => {
      const deltaX = (moveEvent.clientX - startX) / Math.max(1, rect.width);
      const deltaY = (moveEvent.clientY - startY) / Math.max(1, rect.height);
      let patch;

      if (mode === "resize") {
        const width = clamp(original.width + deltaX, 0.025, 1 - original.x);
        const height = clamp(original.height + deltaY, 0.02, 1 - original.y);
        patch = { width, height };
      } else {
        patch = {
          x: clamp(original.x + deltaX, 0, 1 - original.width),
          y: clamp(original.y + deltaY, 0, 1 - original.height),
        };
      }

      if (target.kind === "field") updateField(target.key, patch);
      else updateMask(target.id, patch);
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const addMask = () => {
    const id = `mask-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const mask = {
      id,
      page: pageIndex,
      x: 0.2,
      y: 0.49,
      width: 0.6,
      height: 0.09,
      color: "#FFFFFF",
      opacity: 1,
    };
    emit({ ...layout, maskZones: [...layout.maskZones, mask] }, true);
    setSelection({ kind: "mask", id });
  };

  const removeMask = (id) => {
    emit(
      { ...layout, maskZones: layout.maskZones.filter((mask) => mask.id !== id) },
      true
    );
    setSelection({ kind: "field", key: "participantName" });
  };

  const resetLayout = () => {
    const next = createDefaultCertificateTemplateLayout({ orientation, pageCount });
    setValidationMessage("");
    emit(next, true);
    setSelection({ kind: "field", key: "participantName" });
  };

  const validateAndSave = () => {
    if (!validateLayout(layout)) {
      setValidationMessage(copy.validationError);
      return;
    }
    const next = { ...layout, configured: true };
    setValidationMessage("");
    emit(next, false);
  };

  const selectedField = selection.kind === "field"
    ? layout.fields?.[selection.key]
    : null;
  const selectedMask = selection.kind === "mask"
    ? layout.maskZones.find((mask) => mask.id === selection.id)
    : null;

  return (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[150] overflow-y-auto bg-slate-100 p-3 sm:p-5"
          : "rounded-[1.75rem] border border-slate-200 bg-slate-50 p-4 shadow-inner sm:p-5"
      }
    >
      <div
        className={
          isFullscreen
            ? "mx-auto max-w-[1900px] rounded-[1.75rem] border border-slate-200 bg-slate-50 p-4 shadow-2xl sm:p-5"
            : ""
        }
      >
      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm font-black text-slate-950">{copy.dragHint}</p>
          <p className="mt-1 text-xs font-semibold text-slate-500">{copy.maskHint}</p>
          <p className="mt-1 text-xs font-bold text-blue-700">{copy.fullscreenHint}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => changeZoom(zoom - 0.15)}
              disabled={zoom <= 0.6}
              aria-label="Zoom out"
              className="flex h-9 w-9 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:opacity-30"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => changeZoom(1)}
              title={copy.normalSize}
              className="min-w-[72px] border-x border-slate-200 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-100"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => changeZoom(zoom + 0.15)}
              disabled={zoom >= 2}
              aria-label="Zoom in"
              className="flex h-9 w-9 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsFullscreen((current) => !current)}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-black text-violet-800 transition hover:bg-violet-100"
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
            {isFullscreen ? copy.closeFullscreen : copy.enlarge}
          </button>
          <button
            type="button"
            onClick={resetLayout}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-100"
          >
            <RotateCcw className="h-4 w-4" />
            {copy.reset}
          </button>
          <button
            type="button"
            onClick={validateAndSave}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-black text-white shadow-sm ${
              layout.configured
                ? "bg-emerald-600"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            <CheckCircle2 className="h-4 w-4" />
            {layout.configured ? copy.validated : copy.validate}
          </button>
        </div>
      </div>

      {validationMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {validationMessage}
        </div>
      )}

      <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <div>
          <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
            <button
              type="button"
              disabled={pageIndex <= 0}
              onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
              title={copy.previous}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-black text-slate-700">
              {copy.page} {pageIndex + 1} / {pageCount}
            </span>
            <button
              type="button"
              disabled={pageIndex >= pageCount - 1}
              onClick={() => setPageIndex((current) => Math.min(pageCount - 1, current + 1))}
              title={copy.next}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div
            className={`overflow-auto rounded-2xl border border-slate-300 bg-slate-200 p-3 ${
              isFullscreen
                ? "max-h-[calc(100vh-185px)]"
                : "max-h-[78vh] min-h-[520px]"
            }`}
          >
            <div
              ref={wrapperRef}
              className="relative mx-auto shrink-0 overflow-hidden bg-white shadow-xl"
              style={{
                width: `${previewWidth}px`,
                maxWidth: "none",
              }}
            >
              <canvas ref={canvasRef} className="block h-auto w-full" />

              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                  <div className="flex items-center gap-2 text-sm font-black text-slate-700">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    {copy.loading}
                  </div>
                </div>
              )}

              {layout.maskZones
                .filter((mask) => Number(mask.page || 0) === pageIndex)
                .map((mask) => (
                  <div
                    key={mask.id}
                    onPointerDown={(event) =>
                      startInteraction(event, { kind: "mask", id: mask.id }, "drag")
                    }
                    className={`absolute cursor-move border-2 ${
                      selection.kind === "mask" && selection.id === mask.id
                        ? "border-amber-500"
                        : "border-amber-300"
                    }`}
                    style={{
                      left: `${mask.x * 100}%`,
                      top: `${mask.y * 100}%`,
                      width: `${mask.width * 100}%`,
                      height: `${mask.height * 100}%`,
                      backgroundColor: mask.color,
                      opacity: mask.opacity,
                      zIndex: 10,
                    }}
                  >
                    <span className="absolute left-1 top-1 rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-black text-white">
                      MASK
                    </span>
                    <button
                      type="button"
                      aria-label="Resize"
                      onPointerDown={(event) =>
                        startInteraction(event, { kind: "mask", id: mask.id }, "resize")
                      }
                      className="absolute bottom-0 right-0 h-4 w-4 translate-x-1/2 translate-y-1/2 cursor-se-resize rounded-sm border border-amber-700 bg-amber-400"
                    />
                  </div>
                ))}

              {fieldDefinitions.map(({ key, label, sample }) => {
                const field = layout.fields?.[key];
                if (!field?.enabled || Number(field.page || 0) !== pageIndex) return null;
                const selected = selection.kind === "field" && selection.key === key;
                return (
                  <div
                    key={key}
                    onPointerDown={(event) =>
                      startInteraction(event, { kind: "field", key }, "drag")
                    }
                    className={`absolute flex cursor-move select-none items-center justify-center overflow-hidden border-2 border-dashed bg-blue-500/10 ${
                      selected ? "border-blue-600 ring-2 ring-blue-200" : "border-blue-400"
                    }`}
                    style={{
                      left: `${field.x * 100}%`,
                      top: `${field.y * 100}%`,
                      width: `${field.width * 100}%`,
                      height: `${field.height * 100}%`,
                      zIndex: 20,
                    }}
                  >
                    {key === "qrCode" ? (
                      <div className="grid h-[82%] w-[82%] grid-cols-8 bg-white p-[2px]">
                        {qrPattern.flatMap((row, rowIndex) =>
                          row.map((filled, columnIndex) => (
                            <span
                              key={`${rowIndex}-${columnIndex}`}
                              className={filled ? "bg-slate-950" : "bg-white"}
                            />
                          ))
                        )}
                      </div>
                    ) : (
                      <span
                        className="w-full truncate px-1 text-center font-bold"
                        style={{
                          color: field.color,
                          fontSize: `${clamp(field.fontSize / 2.2, 8, 24)}px`,
                          textAlign: field.alignment,
                          textTransform: field.uppercase ? "uppercase" : "none",
                        }}
                      >
                        {sample}
                      </span>
                    )}
                    <span className="absolute left-1 top-1 max-w-[80%] truncate rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-black text-white">
                      {label}
                    </span>
                    <button
                      type="button"
                      aria-label="Resize"
                      onPointerDown={(event) =>
                        startInteraction(event, { kind: "field", key }, "resize")
                      }
                      className="absolute bottom-0 right-0 h-4 w-4 translate-x-1/2 translate-y-1/2 cursor-se-resize rounded-sm border border-blue-800 bg-blue-500"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {loadError && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {copy.loadError} {loadError}
            </div>
          )}
        </div>

        <aside className="space-y-4 2xl:sticky 2xl:top-4 2xl:self-start">
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Type className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-black text-slate-950">{copy.fields}</h4>
            </div>

            <p className="mb-3 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-bold leading-5 text-blue-800">
              {copy.chooseFields}
            </p>

            <div className="space-y-2">
              {fieldDefinitions.map(({ key, label }) => {
                const enabled = Boolean(layout.fields?.[key]?.enabled);
                const required = key === "participantName";
                const selected =
                  selection.kind === "field" && selection.key === key;

                return (
                  <div
                    key={key}
                    className={`rounded-xl border px-3 py-2.5 transition ${
                      selected
                        ? "border-blue-400 bg-blue-50 ring-1 ring-blue-100"
                        : enabled
                          ? "border-emerald-200 bg-emerald-50/40"
                          : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        id={`certificate-field-${key}`}
                        type="checkbox"
                        checked={enabled}
                        disabled={required}
                        onChange={(event) =>
                          toggleField(key, event.target.checked)
                        }
                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
                      />

                      <button
                        type="button"
                        disabled={!enabled}
                        onClick={() =>
                          setSelection({ kind: "field", key })
                        }
                        className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
                      >
                        {key === "qrCode" ? (
                          <QrCode
                            className={`h-4 w-4 shrink-0 ${
                              enabled
                                ? "text-blue-600"
                                : "text-slate-300"
                            }`}
                          />
                        ) : (
                          <Grip
                            className={`h-4 w-4 shrink-0 ${
                              enabled
                                ? "text-blue-500"
                                : "text-slate-300"
                            }`}
                          />
                        )}

                        <span
                          className={`truncate text-xs font-black ${
                            enabled
                              ? "text-slate-800"
                              : "text-slate-400"
                          }`}
                        >
                          {label}
                        </span>
                      </button>

                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black ${
                          required
                            ? "bg-blue-100 text-blue-800"
                            : enabled
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {required
                          ? copy.required
                          : enabled
                            ? copy.included
                            : copy.excluded}
                      </span>
                    </div>

                    {required && (
                      <p className="mt-2 pl-7 text-[11px] font-semibold text-blue-700">
                        {copy.participantNameRequired}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Square className="h-4 w-4 text-amber-600" />
                <h4 className="text-sm font-black text-slate-950">{copy.masks}</h4>
              </div>
              <button
                type="button"
                onClick={addMask}
                className="rounded-lg bg-amber-100 p-2 text-amber-800 hover:bg-amber-200"
                title={copy.addMask}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {layout.maskZones.filter((mask) => mask.page === pageIndex).length === 0 ? (
                <p className="text-xs font-semibold leading-5 text-slate-500">{copy.noMask}</p>
              ) : (
                layout.maskZones
                  .filter((mask) => mask.page === pageIndex)
                  .map((mask, index) => (
                    <button
                      key={mask.id}
                      type="button"
                      onClick={() => setSelection({ kind: "mask", id: mask.id })}
                      className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-xs font-bold ${
                        selection.kind === "mask" && selection.id === mask.id
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-slate-200 text-slate-600"
                      }`}
                    >
                      Zone {index + 1}
                      <span className="h-4 w-4 rounded border border-slate-300" style={{ background: mask.color }} />
                    </button>
                  ))
              )}
            </div>
          </section>

          {(selectedField || selectedMask) && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <h4 className="mb-4 text-sm font-black text-slate-950">{copy.settings}</h4>
              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-slate-600">{copy.page}</span>
                  <select
                    value={(selectedField || selectedMask).page}
                    onChange={(event) => {
                      const page = Number(event.target.value);
                      if (selectedField) updateField(selection.key, { page });
                      else updateMask(selection.id, { page });
                      setPageIndex(page);
                    }}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700"
                  >
                    {Array.from({ length: pageCount }, (_, index) => (
                      <option key={index} value={index}>
                        {copy.page} {index + 1}
                      </option>
                    ))}
                  </select>
                </label>

                {selectedField?.type === "text" && (
                  <>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-slate-600">{copy.fontSize}</span>
                      <input
                        type="number"
                        min="6"
                        max="96"
                        value={selectedField.fontSize}
                        onChange={(event) => updateField(selection.key, { fontSize: Number(event.target.value) })}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-slate-600">{copy.font}</span>
                      <select
                        value={selectedField.fontFamily}
                        onChange={(event) => updateField(selection.key, { fontFamily: event.target.value })}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold"
                      >
                        <option value="Helvetica">Helvetica</option>
                        <option value="Helvetica-Bold">Helvetica Bold</option>
                        <option value="Times-Roman">Times Roman</option>
                        <option value="Times-Bold">Times Bold</option>
                        <option value="Courier">Courier</option>
                        <option value="Courier-Bold">Courier Bold</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-slate-600">{copy.alignment}</span>
                      <select
                        value={selectedField.alignment}
                        onChange={(event) => updateField(selection.key, { alignment: event.target.value })}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold"
                      >
                        <option value="left">{copy.left}</option>
                        <option value="center">{copy.center}</option>
                        <option value="right">{copy.right}</option>
                      </select>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-slate-600">{copy.color}</span>
                        <input
                          type="color"
                          value={selectedField.color}
                          onChange={(event) => updateField(selection.key, { color: event.target.value })}
                          className="h-10 w-full rounded-xl border border-slate-200 p-1"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold text-slate-600">{copy.maxLines}</span>
                        <input
                          type="number"
                          min="1"
                          max="6"
                          value={selectedField.maxLines}
                          onChange={(event) => updateField(selection.key, { maxLines: Number(event.target.value) })}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold"
                        />
                      </label>
                    </div>
                    <label className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                      <span className="text-xs font-bold text-slate-600">{copy.uppercase}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedField.uppercase)}
                        onChange={(event) => updateField(selection.key, { uppercase: event.target.checked })}
                      />
                    </label>
                  </>
                )}

                {selectedMask && (
                  <>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-slate-600">{copy.color}</span>
                      <input
                        type="color"
                        value={selectedMask.color}
                        onChange={(event) => updateMask(selection.id, { color: event.target.value })}
                        className="h-10 w-full rounded-xl border border-slate-200 p-1"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeMask(selection.id)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-50 px-4 py-2.5 text-xs font-black text-rose-700 hover:bg-rose-100"
                    >
                      <Trash2 className="h-4 w-4" />
                      {copy.deleteMask}
                    </button>
                  </>
                )}
              </div>
            </section>
          )}

          <div className={`rounded-2xl border p-4 text-xs font-bold leading-5 ${
            layout.configured
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-blue-200 bg-blue-50 text-blue-800"
          }`}>
            {layout.configured ? copy.validated : copy.mustValidate}
          </div>
        </aside>
      </div>
      </div>
    </div>
  );
};

export default CertificateTemplateDesigner;
